/**
 * One example row of the draft table, with the six columns of record (DRAFT_COLUMNS) and
 * the icons of the case page, on invented data. Module 4 shows it before the lesson on
 * the real table. The table form is a one-row DataGrid; the list form (a phone, the
 * Outlook pane) is the Card of the case page's list form with the same cells as labelled
 * lines. No row button, no chevron: the figure explains, it does not work.
 */

import { Icon } from "@norican/icons";
import { Badge, Card, DataGrid, type DataGridColumnDef } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import {
  BASIS_ICON,
  BASIS_LABEL,
  LEAD_ICON,
  PRICE_ICON,
  SOURCE_ICON,
  SOURCE_LABEL,
  STATUS_ICON,
  STATUS_TONE,
} from "@/lib/line-icons";
import { DRAFT_COLUMNS, LINE_STATUS_WORDS } from "@/lib/sales-flow";

import { Figure } from "./figure";

export const TABLE_ROW_TITLE = "Example row of the draft";

/** The one invented line: a blade found by an alias, in band, lead time known. */
export interface ExampleLine {
  id: string;
  asked: string;
  itemNumber: string;
  itemName: string;
  qty: string;
  price: string;
  lead: string;
}

export const EXAMPLE_LINE: ExampleLine = {
  id: "example-1",
  asked: "2 x blade type SB-12",
  itemNumber: "0990012",
  itemName: "Blade, shot blast wheel",
  qty: "2 pcs",
  price: "EUR 1,240.00",
  lead: "21 days",
};

/** The cells, one renderer per column, shared by the table and the list form. */
const CELLS: Record<keyof typeof DRAFT_COLUMNS, (line: ExampleLine) => ReactNode> = {
  status: () => (
    <Badge tone={STATUS_TONE.confirm} variant="subtle" size="sm" data-line-word="confirm">
      <Icon icon={STATUS_ICON.confirm} size="xs" />
      {LINE_STATUS_WORDS.confirm}
    </Badge>
  ),
  asked: (line) => (
    <span className="flex min-w-0 items-start gap-1.5">
      <Icon
        icon={SOURCE_ICON.mail}
        size="xs"
        label={SOURCE_LABEL.mail}
        className="mt-0.5 shrink-0"
      />
      <span className="break-words">{line.asked}</span>
    </span>
  ),
  proposed: (line) => (
    <span className="flex min-w-0 items-start gap-1.5">
      <Icon
        icon={BASIS_ICON.alias}
        size="xs"
        label={BASIS_LABEL.alias}
        className="mt-0.5 shrink-0"
      />
      <span className="break-words">
        <strong className="tabular-nums">{line.itemNumber}</strong> {line.itemName}
      </span>
    </span>
  ),
  qty: (line) => <span className="tabular-nums">{line.qty}</span>,
  price: (line) => (
    <span className="flex items-center gap-1.5 tabular-nums">
      <Icon icon={PRICE_ICON.in_band} size="xs" tone="success" label="In band" />
      {line.price}
    </span>
  ),
  lead: (line) => (
    <span className="flex items-center gap-1.5 tabular-nums">
      <Icon icon={LEAD_ICON.known} size="xs" className="shrink-0" />
      {line.lead}
    </span>
  ),
};

/** The six leaf columns with the widths of the case page's table (136 / flex / flex / 80 / 136 / 104). */
const COLUMNS: DataGridColumnDef<ExampleLine>[] = [
  { id: "status", header: DRAFT_COLUMNS.status, width: 136, cell: CELLS.status },
  { id: "asked", header: DRAFT_COLUMNS.asked, cell: CELLS.asked },
  { id: "proposed", header: DRAFT_COLUMNS.proposed, cell: CELLS.proposed },
  { id: "qty", header: DRAFT_COLUMNS.qty, width: 80, align: "end", cell: CELLS.qty },
  { id: "price", header: DRAFT_COLUMNS.price, width: 136, align: "end", cell: CELLS.price },
  { id: "lead", header: DRAFT_COLUMNS.lead, width: 104, cell: CELLS.lead },
];

const LIST_ORDER: readonly (keyof typeof DRAFT_COLUMNS)[] = [
  "status",
  "asked",
  "proposed",
  "qty",
  "price",
  "lead",
];

export interface TableRowFigureProps {
  /** The table (default) or the list form of a phone and the Outlook pane; the narrow media query picks the list. */
  layout?: "table" | "list";
  caption?: string;
}

export function TableRowFigure({ layout, caption }: TableRowFigureProps) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = layout ?? (narrow ? "list" : "table");
  return (
    <Figure name="table-row" title={TABLE_ROW_TITLE} caption={caption}>
      {form === "list" ? (
        <Card
          variant="outline"
          padding="none"
          accent="left"
          accentTone="accent"
          data-layout="list"
          className="min-w-0"
        >
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 p-3 text-sm">
            {LIST_ORDER.map((key) => (
              <div key={key} className="contents">
                <dt className="text-fg-subtle pt-0.5 text-xs">{DRAFT_COLUMNS[key]}</dt>
                <dd className="min-w-0">{CELLS[key](EXAMPLE_LINE)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      ) : (
        <DataGrid<ExampleLine>
          columns={COLUMNS}
          data={[EXAMPLE_LINE]}
          getRowId={(line) => line.id}
          tableLayout="fixed"
          density="compact"
          aria-label={TABLE_ROW_TITLE}
          data-layout="table"
        />
      )}
    </Figure>
  );
}
